import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchTradeIconSvg } from '@apis/storage';
import { sanitizeSvg } from '@utils/sanitizeSvg';
import styles from './tradeIcon.module.css';
import { TRADE_ICON_FILTER } from './tradeIconFilter';

type ITradeIconProps = {
  tradeId: string;
  /** Icon size — width = height. A number is px (default 14, to fit inside chip
   *  pills); a CSS length string (e.g. `'1em'`) passes through so callers can
   *  size the pictogram relative to the surrounding font — which lets it scale
   *  with the scheduler's geometric zoom. */
  size?: number | string;
  /** CSS `filter` to apply to the SVG. Defaults to white (chip-on-pill).
   *  Override or set to `none` for default-coloured icons. */
  filter?: string;
};

/**
 * Per-trade pictogram fetched lazily from the backend storage proxy
 * (`/api/sites/:siteId/storage/trade-icon/:tradeId`). Mirrors trusstor's
 * `TradeIconShared` — the bytes are content-addressable by tradeId so we
 * cache aggressively in TanStack Query (24h gc + stale time). Returns null
 * when the upstream has no icon for the trade so the chip can render
 * without a pictogram instead of breaking.
 */
export function TradeIcon({
  tradeId,
  size = 14,
  filter = TRADE_ICON_FILTER.white,
}: ITradeIconProps) {
  const { siteId } = useParams<{ siteId: string }>();
  const { data: svg } = useQuery({
    queryKey: ['storage', 'trade-icon', siteId, tradeId],
    queryFn: ({ signal }) => fetchTradeIconSvg(siteId!, tradeId, signal),
    enabled: Boolean(siteId && tradeId),
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: false,
  });
  if (!svg) return null;
  return (
    <span
      className={styles.icon}
      style={{
        width: size,
        height: size,
        // CSS custom property consumed by `.icon svg { filter: var(--trade-icon-filter, none) }`.
        ['--trade-icon-filter' as string]: filter,
      }}
      // The storage proxy serves admin-uploaded content — sanitise
      // before inlining to close the stored-XSS path. See `sanitizeSvg`
      // for the threat model.
      dangerouslySetInnerHTML={{ __html: sanitizeSvg(svg) }}
    />
  );
}
